/**
 * Demo funnel analytics (SKE-4166, phase 3).
 *
 * The KPI for the public approvals demo is the **email-unlock rate**, so every
 * step of that funnel has to be countable: how many visitors were offered the
 * gate, how many dismissed it, how many submitted, how many failed.
 *
 * TWO SINKS, ON PURPOSE
 * ---------------------
 * 1. **Amplitude** — this app's existing product-analytics channel (`track`
 *    from `@amplitude/analytics-browser`, already initialised in `main.tsx`).
 *    Funnel/KPI reporting lives here.
 * 2. **`window.dataLayer`** — GTM's ingress. external-review does *not* load a
 *    GTM container today (see index.html: Hotjar + FontAwesome only), so this
 *    push is inert right now. It is still made, because the demo is served on a
 *    marketing host (try.skedsocial.com) whose GTM container is expected to be
 *    added at the edge, and the standard GTM snippet contract is "the array
 *    exists before the container loads; the container replays it". Creating the
 *    array here costs nothing and means no code change is needed when the
 *    container lands. Pixels stay a GTM-container concern — nothing here loads
 *    a vendor tag directly.
 *
 * The **server** conversion (GA4 Measurement Protocol via sGTM, and from there
 * every ad platform) is fired by the lead endpoint, not from the browser — see
 * `apps/schedugram-server/src/app/services/publicDemoLead.ts`. The browser
 * events are for funnel reporting; the server event is the conversion of
 * record.
 */

import { track } from '@amplitude/analytics-browser'

import { getDemoAttribution, getDemoSessionId } from './demoAttribution'
import { getDemoStoreSnapshot } from './demoStore'

/**
 * The demo funnel. Kebab-case matches this app's existing `ext-*` event names;
 * the server sanitises to GA4's `[A-Za-z0-9_]` at its own boundary.
 */
export type DemoAnalyticsEvent =
  /**
   * The demo mounted. Fired once per page load, at the top of the funnel — the
   * denominator for everything below, and the only event a bounced visitor
   * produces.
   */
  | 'demo-landed'
  /**
   * The marketer's side became visible for the first time — docked, revealed
   * after an action, or opened by hand. This is the demo's headline "both sides
   * at once" moment, so it is counted separately from landing: a visitor who
   * never sees it did not see the pitch.
   */
  | 'demo-teaser-viewed'
  /** The visitor approved a post. */
  | 'demo-post-approved'
  /** The visitor sent a post back for changes. */
  | 'demo-post-rejected'
  /** The visitor left a note on a post. */
  | 'demo-comment-added'
  /** The email gate became visible to the visitor. Denominator of the KPI. */
  | 'demo-gate-shown'
  /** The visitor closed the gate without giving an email. */
  | 'demo-gate-dismissed'
  /** THE KPI: the visitor submitted an email and the server accepted it. */
  | 'demo-email-submitted'
  /** Submission attempted but rejected (validation, captcha, network). */
  | 'demo-email-failed'
  /**
   * The board was re-skinned to the visitor's own client (phase 4). Fired on
   * every applied brand, including the locally-derived ones — the `source`
   * property is what separates a real brand match from a name-only fallback,
   * so the two are counted together and split in reporting rather than being
   * two events that could drift apart.
   */
  | 'demo-personalized'
  /** The lookup was rejected outright (blocked domain, bad input, rate limit). */
  | 'demo-personalize-failed'
  /**
   * The marketer replied to the visitor's note (phase 5). Fired on every reply
   * the visitor actually sees, including canned ones — the `source` property
   * (`model` | `fallback` | `offline`) is what separates a real completion from
   * a canned line, so the two are counted together and split in reporting
   * rather than being two events that could drift apart. Without that, a
   * misconfigured environment would look like a *drop* in engagement instead of
   * a drop in model coverage.
   */
  | 'demo-ai-reply-shown'
  /**
   * The end-state CTA surface became visible (phase 6). Fired at most once per
   * visit, so it is the denominator for every CTA below — "of the visitors who
   * reached the conversion moment, how many took a next step".
   */
  | 'demo-cta-shown'
  /** The visitor left for signup. The demo's primary conversion. */
  | 'demo-cta-trial'
  /** The visitor left for the sales booking page. */
  | 'demo-cta-book-call'
  /**
   * The visitor asked for the recap email and the server accepted it. Fired
   * only on acceptance, so it counts sent recaps rather than form submits.
   */
  | 'demo-cta-email-summary'
  /** The recap request was rejected (validation, captcha, network). */
  | 'demo-cta-email-summary-failed'

type DemoAnalyticsProps = Record<string, unknown>

interface DataLayerWindow {
  dataLayer?: unknown[]
}

/**
 * Context every demo event carries, so any single event is enough to attribute
 * a visitor without joining back to the pageview.
 */
const baseProps = (): DemoAnalyticsProps => {
  const attribution = getDemoAttribution()

  return {
    demo_session_id: getDemoSessionId(),
    vertical: attribution.vertical ?? null,
    utm_source: attribution.utmSource ?? null,
    utm_medium: attribution.utmMedium ?? null,
    utm_campaign: attribution.utmCampaign ?? null,
    utm_content: attribution.utmContent ?? null,
    utm_term: attribution.utmTerm ?? null,
  }
}

/**
 * Fire one funnel event to both sinks. Never throws: a broken analytics call
 * must not be able to break the demo it is measuring.
 */
export const trackDemo = (
  event: DemoAnalyticsEvent,
  props: DemoAnalyticsProps = {}
): void => {
  const payload = { ...baseProps(), ...props }

  try {
    track(event, payload)
  } catch (error) {
    console.warn('[external-review] demo analytics (amplitude) failed', error)
  }

  try {
    const win = globalThis.window as (Window & DataLayerWindow) | undefined
    if (win) {
      win.dataLayer ??= []
      win.dataLayer.push({ event, ...payload })
    }
  } catch (error) {
    console.warn('[external-review] demo analytics (dataLayer) failed', error)
  }
}

/* -------------------------------------------------------------------------- */
/* Once-per-visit events                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Some events are funnel *denominators* and are only meaningful if they are
 * fired exactly once per visit — "landed" must not count a re-render, and
 * "teaser viewed" must not count the fourth time the rail auto-reveals.
 *
 * The guard lives here rather than in a `useRef` at each call site because more
 * than one component can legitimately observe the same beat (the desktop rail
 * and the mobile sheet both mean "the marketer's side is on screen"), and a
 * per-component ref would let each of them fire it once.
 */
const firedOnce = new Set<DemoAnalyticsEvent>()

export const trackDemoOnce = (
  event: DemoAnalyticsEvent,
  props: DemoAnalyticsProps = {}
): void => {
  if (firedOnce.has(event)) {
    return
  }
  firedOnce.add(event)
  trackDemo(event, props)
}

/** Top of the funnel. See `demo-landed`. */
export const trackDemoLanded = (props: DemoAnalyticsProps = {}): void => {
  trackDemoOnce('demo-landed', {
    // A returning visitor arrives with their board already part-reviewed, so
    // their funnel is not a fresh one — split rather than pooled in reporting.
    is_returning: getDemoStoreSnapshot() !== null,
    ...props,
  })
}

/** The "both sides at once" moment. See `demo-teaser-viewed`. */
export const trackDemoTeaserViewed = (props: DemoAnalyticsProps = {}): void => {
  trackDemoOnce('demo-teaser-viewed', props)
}

/** Test seam — drops the once-per-visit guards so a spec can re-drive them. */
export const resetDemoAnalyticsForTest = (): void => {
  firedOnce.clear()
}
